/**
 * Os componentes da biblioteca comum de interface — specs 0061 e 0086.
 *
 * Nasceram no Fechamento por Cooperado (0061), com a linguagem do TOPZ do
 * `topbrasilpv-bpm` adequada ao padrão deste projeto: classes `fc-` de
 * `interface.css` sobre os tokens de `styles/tokens.css`. Desde a 0086 são a
 * biblioteca do painel inteiro.
 */

import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { X } from 'lucide-react';

// ── Janela ──────────────────────────────────────────────────────────────────

/**
 * O tipo da janela (0086). Todos têm a mesma base — Esc, foco preso, erro dentro,
 * rodapé que diz o que fica registrado — e mudam de forma:
 * - `padrao`: decisão e formulário, no centro;
 * - `larga`: um registro inteiro, com abas (`AbasDaJanela`);
 * - `gaveta`: consulta ao lado, sem esconder a tela;
 * - `documento`: leitura de documento, quase em tela cheia.
 */
export type TipoDeJanela = 'padrao' | 'larga' | 'gaveta' | 'documento';

interface PropsDaJanela {
  aberta: boolean;
  titulo: string;
  /** Uma frase sobre o que a janela faz, abaixo do título. */
  contexto?: React.ReactNode;
  /**
   * A consequência, no rodapé à esquerda. O padrão diz que fica registrado —
   * é verdade para tudo que grava neste módulo, e dizer é o que o bpm faz.
   */
  apoio?: React.ReactNode;
  /** O erro aparece AQUI, dentro da janela, e não no topo da página atrás do fundo. */
  erro?: string | null;
  ocupado?: boolean;
  /** Atalho antigo para `tipo="larga"`. */
  larga?: boolean;
  tipo?: TipoDeJanela;
  onFechar: () => void;
  /** Os botões do rodapé, à direita. Gaveta e documento podem não ter. */
  acoes?: React.ReactNode;
  /** Classe a mais no conteúdo, para a tela ajustar a largura (0113: a ficha fiscal). */
  className?: string;
  children: React.ReactNode;
}

/**
 * O modal do módulo, sobre Radix.
 *
 * Substitui três `div` feitas à mão — Novo lançamento, cadastro fiscal e a
 * decisão da conciliação — que não fechavam com Esc, não prendiam o foco e
 * mostravam o erro no topo da página, **atrás** do próprio fundo escuro.
 */
export const Janela: React.FC<PropsDaJanela> = ({
  aberta, titulo, contexto, apoio = 'Fica registrado na trilha da competência.',
  erro, ocupado, larga, tipo, onFechar, acoes, className, children,
}) => {
  const forma = tipo ?? (larga ? 'larga' : 'padrao');
  // O erro mora no topo do corpo, que rola: quem clicou no rodapé depois de
  // rolar não o veria. Ele vem até a vista (0108); o role=alert anuncia.
  const caixaDoErro = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (erro) caixaDoErro.current?.scrollIntoView({ block: 'nearest' });
  }, [erro]);
  return (
  <DialogPrimitive.Root open={aberta} onOpenChange={(abre) => { if (!abre && !ocupado) onFechar(); }}>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fc-janela-fundo" />
      <DialogPrimitive.Content
        className={`fc-janela fc-janela-${forma}${forma === 'larga' ? ' fc-janela-larga' : ''}${className ? ` ${className}` : ''}`}
        aria-describedby={undefined}
        // Enquanto grava, fechar perderia o resultado sem aviso.
        onEscapeKeyDown={(e) => { if (ocupado) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (ocupado) e.preventDefault(); }}
      >
        <header className="fc-janela-cabecalho">
          <div>
            <DialogPrimitive.Title className="fc-janela-titulo">{titulo}</DialogPrimitive.Title>
            {contexto && <p className="fc-janela-contexto">{contexto}</p>}
          </div>
          <DialogPrimitive.Close className="fc-btn-icone" aria-label="Fechar" disabled={ocupado}>
            <X size={16} />
          </DialogPrimitive.Close>
        </header>

        {/* Rolável: recebe foco para quem usa só o teclado conseguir rolar (0091). */}
        <div className="fc-janela-corpo" tabIndex={0}>
          {erro && <div ref={caixaDoErro} className="fc-janela-erro" role="alert">{erro}</div>}
          <div className="fc-janela-campos">{children}</div>
        </div>

        {(apoio || acoes) && (
          <footer className="fc-janela-rodape">
            <span className="fc-janela-apoio">{apoio}</span>
            {acoes && <div className="fc-janela-acoes">{acoes}</div>}
          </footer>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
  );
};

/**
 * As abas de uma janela larga (0086): o registro inteiro sem sair da lista.
 * Sobre Radix Tabs — setas, Home e End movem entre as abas — e cada painel só
 * monta quando é aberto, para carregar sob demanda e ter o próprio erro.
 */
export const AbasDaJanela: React.FC<{
  abas: Array<{ chave: string; rotulo: string; conteudo: () => React.ReactNode }>;
  inicial?: string;
  rotulo: string;
  /**
   * Controlada (0088): o formulário que valida ao salvar leva a pessoa até a
   * aba do campo que falta. Sem `valor`, a janela escolhe sozinha.
   */
  valor?: string;
  onMudar?: (chave: string) => void;
}> = ({ abas, inicial, rotulo, valor, onMudar }) => (
  <TabsPrimitive.Root
    {...(valor !== undefined ? { value: valor } : { defaultValue: inicial ?? abas[0]?.chave })}
    onValueChange={onMudar}
    className="fc-abas"
  >
    <TabsPrimitive.List className="fc-abas-lista" aria-label={rotulo}>
      {abas.map((a) => (
        <TabsPrimitive.Trigger key={a.chave} value={a.chave} className="fc-aba">{a.rotulo}</TabsPrimitive.Trigger>
      ))}
    </TabsPrimitive.List>
    {abas.map((a) => (
      <TabsPrimitive.Content key={a.chave} value={a.chave} className="fc-aba-painel">{a.conteudo()}</TabsPrimitive.Content>
    ))}
  </TabsPrimitive.Root>
);

interface PropsDoCampo {
  rotulo: string;
  /** Ocupa as duas colunas da janela. */
  largo?: boolean;
  ajuda?: React.ReactNode;
  children: React.ReactNode;
}

/** Rótulo em caixa alta sobre o controle, como no TOPZ. */
export const CampoDaJanela: React.FC<PropsDoCampo> = ({ rotulo, largo, ajuda, children }) => (
  <label className={`fc-janela-campo${largo ? ' fc-janela-campo-largo' : ''}`}>
    <span className="fc-janela-rotulo">{rotulo}</span>
    {children}
    {ajuda && <small className="fc-janela-ajuda">{ajuda}</small>}
  </label>
);

/**
 * Escolha de duas ou três opções dentro da janela (0088): o toggle-pill do
 * painel, com `<input type="radio">` nativo. É o campo booleano editável do
 * projeto — setas trocam a opção, o leitor de tela lê o grupo e a escolha.
 *
 * Não é `CampoDaJanela`: aquele é um `<label>`, e um grupo de rádios dentro de
 * um rótulo só teria um nome para as duas opções.
 */
export const PilulaDaJanela: React.FC<{
  nome: string;
  rotulo: string;
  opcoes: Array<{ valor: string; rotulo: string }>;
  valor: string;
  onMudar: (valor: string) => void;
  desabilitado?: boolean;
  largo?: boolean;
  ajuda?: React.ReactNode;
}> = ({ nome, rotulo, opcoes, valor, onMudar, desabilitado, largo, ajuda }) => (
  <div
    className={`fc-janela-campo${largo ? ' fc-janela-campo-largo' : ''}`}
    role="radiogroup"
    aria-labelledby={`${nome}-rotulo`}
  >
    <span id={`${nome}-rotulo`} className="fc-janela-rotulo">{rotulo}</span>
    <div className="toggle-pill">
      {opcoes.map((o) => (
        <React.Fragment key={o.valor}>
          <input
            type="radio"
            id={`${nome}-${o.valor}`}
            name={nome}
            value={o.valor}
            checked={valor === o.valor}
            disabled={desabilitado}
            onChange={() => onMudar(o.valor)}
          />
          <label htmlFor={`${nome}-${o.valor}`}>{o.rotulo}</label>
        </React.Fragment>
      ))}
    </div>
    {ajuda && <small className="fc-janela-ajuda">{ajuda}</small>}
  </div>
);

/**
 * Dinheiro com o `R$` dentro da caixa e o valor à direita.
 *
 * Alinhado à direita porque é assim que se lê número — e é como a coluna de
 * valor aparece em todas as tabelas do módulo.
 */
export const EntradaDeMoeda: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <span className="fc-moeda">
    <span className="fc-moeda-prefixo" aria-hidden="true">R$</span>
    <input type="number" step="0.01" inputMode="decimal" {...props} />
  </span>
);

// ── Os blocos de leitura ─────────────────────────────────────────────────────

/** O papel de uma cor. Nunca a cor em si: verde é "sucesso", não "botão". */
export type Papel = 'sucesso' | 'atencao' | 'perigo' | 'info' | 'neutro';

/** Pílula de estado. Sempre com texto — a cor não carrega significado sozinha. */
export const Selo: React.FC<{ papel: Papel; title?: string; children: React.ReactNode }> = ({
  papel, title, children,
}) => <span className={`fc-selo fc-selo-${papel}`} title={title}>{children}</span>;

interface PropsDoKpi {
  rotulo: string;
  valor: React.ReactNode;
  detalhe?: React.ReactNode;
  papel?: Papel;
  /** Há algo a resolver: o cartão vira sólido, como o "atrasadas" do bpm. */
  alerta?: boolean;
  /** Quando o KPI também filtra a lista (os degraus do Pagamento). */
  onClick?: () => void;
  ativo?: boolean;
  title?: string;
}

/**
 * O número que resume a tela.
 *
 * Hierarquia por PESO, que é o que o TOPZ faz: rótulo pequeno em caixa alta,
 * número grande em 700. O módulo tinha o peso 700 uma única vez em 1.631
 * linhas de CSS — número, título e rótulo pesavam o mesmo.
 */
export const Kpi: React.FC<PropsDoKpi> = ({
  rotulo, valor, detalhe, papel = 'neutro', alerta, onClick, ativo, title,
}) => {
  const classe = `fc-kpi fc-kpi-${alerta ? 'alerta' : papel}${ativo ? ' fc-kpi-ativo' : ''}`;
  const conteudo = (
    <>
      <span className="fc-kpi-rotulo">{rotulo}</span>
      <b className="fc-kpi-valor">{valor}</b>
      {detalhe && <small className="fc-kpi-detalhe">{detalhe}</small>}
    </>
  );
  return onClick
    ? <button type="button" className={classe} onClick={onClick} aria-pressed={ativo} title={title}>{conteudo}</button>
    : <div className={classe} title={title}>{conteudo}</div>;
};

/** A linha de KPIs: quatro por linha, dois abaixo de 992px. */
export const LinhaDeKpis: React.FC<{ children: React.ReactNode; rotulo?: string }> = ({ children, rotulo }) => {
  // Cinco indicadores numa grade de quatro deixavam o quinto sozinho numa
  // segunda linha (0091): a grade acompanha a quantidade.
  const cinco = React.Children.toArray(children).length === 5;
  return <section className={cinco ? 'fc-kpis fc-kpis-5' : 'fc-kpis'} aria-label={rotulo}>{children}</section>;
};

/** O cartão de conteúdo. Página: branco e sombra, sem borda. */
export const Cartao: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <section className={`fc-cartao${className ? ` ${className}` : ''}`}>{children}</section>
);

/** Rótulo em caixa alta à esquerda, ação à direita, linha embaixo. */
export const CabecalhoDeSecao: React.FC<{
  titulo: string; contagem?: number; children?: React.ReactNode;
}> = ({ titulo, contagem, children }) => (
  <header className="fc-secao-cabecalho">
    <h3 className="fc-secao-titulo">
      {titulo}
      {contagem !== undefined && <span className="fc-secao-contagem">{contagem}</span>}
    </h3>
    {children && <div className="fc-secao-acao">{children}</div>}
  </header>
);

/** Par rótulo/valor. Valor ausente vira "—": o campo nunca some. */
export const Campo: React.FC<{ rotulo: string; valor?: React.ReactNode }> = ({ rotulo, valor }) => (
  <div className="fc-campo">
    <span className="fc-campo-rotulo">{rotulo}</span>
    <span className="fc-campo-valor">
      {valor === null || valor === undefined || valor === '' ? '—' : valor}
    </span>
  </div>
);

/**
 * Por que o botão ao lado está apagado.
 *
 * O `title` não aparece no toque nem para quem não para o cursor em cima. O bpm
 * escreveu a regra: "ficar apagado está certo; calar o motivo não".
 */
export const MotivoDoBloqueio: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="fc-motivo" role="note">{children}</p>
);

/**
 * A resposta da tela, no topo.
 *
 * Cada etapa tem uma pergunta principal — "posso apurar?", "está pronto para
 * fechar?" — e a resposta morava onde sobrava: na Pendências, num parágrafo
 * solto DEPOIS da lista. O veredito vem antes de tudo, com o próximo passo.
 */
export const Veredito: React.FC<{
  papel: Papel; titulo: React.ReactNode; children?: React.ReactNode; acao?: React.ReactNode;
}> = ({ papel, titulo, children, acao }) => (
  <section className={`fc-veredito fc-veredito-${papel}`} role="status">
    <div className="fc-veredito-texto">
      <b className="fc-veredito-titulo">{titulo}</b>
      {children && <span className="fc-veredito-detalhe">{children}</span>}
    </div>
    {acao && <div className="fc-veredito-acao">{acao}</div>}
  </section>
);


// ── Os estados (0086) ────────────────────────────────────────────────────────

/**
 * Carregando, em esqueleto: a forma do que vem, não um giro no meio da tela.
 * `aria-busy` avisa o leitor de tela; sem animação para quem pediu menos
 * movimento (o CSS respeita `prefers-reduced-motion`).
 */
export const Carregando: React.FC<{ rotulo?: string; linhas?: number }> = ({
  rotulo = 'Carregando', linhas = 4,
}) => (
  <div className="fc-carregando" aria-busy="true" aria-label={rotulo} role="status">
    {Array.from({ length: linhas }, (_, i) => <i key={i} />)}
  </div>
);

/**
 * Vazio que orienta: o que não há, por quê, e o que fazer. Substitui o
 * "Nenhum registro" solto e o 📋 do `ModuleNoData`.
 */
export const EstadoVazio: React.FC<{
  titulo: React.ReactNode; children?: React.ReactNode; acao?: React.ReactNode;
}> = ({ titulo, children, acao }) => (
  <div className="fc-vazio">
    <b className="fc-vazio-titulo">{titulo}</b>
    {children && <p className="fc-vazio-texto">{children}</p>}
    {acao}
  </div>
);

/**
 * O que deu certo, na própria tela (`role=status`). Substitui o `alert` de
 * sucesso: não interrompe, e fica visível até a próxima ação.
 */
export const Aviso: React.FC<{ children: React.ReactNode; onFechar?: () => void }> = ({ children, onFechar }) => (
  <div className="fc-aviso" role="status">
    <span>{children}</span>
    {onFechar && (
      <button type="button" className="fc-btn-icone" aria-label="Dispensar o aviso" onClick={onFechar}>
        <X size={14} />
      </button>
    )}
  </div>
);
